import { useState, useEffect, useCallback } from "react";
import { Loader2, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { AccountShell } from "../../components/account/AccountShell";
import { useAuth } from "../../hooks/useAuth";
import { getInvoices, downloadInvoicePdf } from "../../lib/api";
import { fmtDate, fmtMoney } from "../../lib/format";

export default function AccountInvoices() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getInvoices()
      .then(setInvoices)
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      await downloadInvoicePdf(inv.id, inv.invoice_number, inv.pdf_url);
    } catch {
      toast.error("Could not download invoice.");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <AccountShell title="Invoice History" subtitle="Download receipts for all your payments." testid="account-invoices-page">
      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#687280]">
          <Loader2 className="animate-spin" size={22} />
        </div>
      ) : invoices.length === 0 ? (
        <div className="bg-[#F7F7F7] rounded-2xl p-10 text-center" data-testid="no-invoices-state">
          <div className="w-12 h-12 rounded-xl bg-white text-[#D4AF37] flex items-center justify-center mx-auto mb-4">
            <FileText size={22} />
          </div>
          <p className="font-display font-semibold text-[#0F0F0F]">No invoices yet</p>
          <p className="text-sm text-[#687280] mt-1">Your paid receipts will appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden" data-testid="invoices-table">
          {/* header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3.5 bg-[#FAFAFA] border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF]">
            <div className="col-span-3">Date</div>
            <div className="col-span-4">Description</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Invoice</div>
          </div>
          {invoices.map((inv) => (
            <div
              key={inv.id}
              className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 border-b border-gray-50 last:border-0 items-center"
              data-testid={`invoice-row-${inv.id}`}
            >
              <div className="sm:col-span-3 text-sm font-medium text-[#0F0F0F]">{fmtDate(inv.date)}</div>
              <div className="sm:col-span-4 text-sm text-[#687280] order-last sm:order-none col-span-2">{inv.description}</div>
              <div className="sm:col-span-2 text-sm font-semibold text-[#0F0F0F]">{fmtMoney(inv.amount, inv.currency)}</div>
              <div className="sm:col-span-1">
                <span className="inline-flex rounded-full bg-emerald-50 text-emerald-600 px-2.5 py-0.5 text-xs font-semibold">
                  {inv.status}
                </span>
              </div>
              <div className="sm:col-span-2 sm:text-right">
                <button
                  onClick={() => download(inv)}
                  disabled={downloading === inv.id}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-1.5 text-xs font-semibold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors disabled:opacity-60"
                  data-testid={`download-invoice-${inv.id}`}
                >
                  {downloading === inv.id ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
